import {forwardRef, useRef, type ComponentPropsWithoutRef, type ElementType, type ReactNode} from "react";
import {cn} from "../lib/utils";
import {Tooltip, TooltipContent, TooltipTrigger} from "../Tooltip";
import {useIsTruncated} from "./hooks/useIsTruncated";

export interface TypographyProps extends ComponentPropsWithoutRef<"p"> {
	variant?: TypographyVariant;
	component?: ElementType;
	align?: TypographyAlign;
	color?: TypographyColor;
	gutterBottom?: boolean;
	noWrap?: boolean;
	isTruncated?: boolean;
	children?: ReactNode;
}

export const Typography = forwardRef<HTMLElement, TypographyProps>((props, ref) => {
	if (props.isTruncated) return <TypographyWithTruncation {...props} ref={ref}/>;
	return <TypographyBase {...props} ref={ref}/>;
});

Typography.displayName = "Typography";

const TypographyBase = forwardRef<HTMLElement, TypographyProps>(({
	variant = "body3",
	component,
	align,
	color = "textPrimary",
	gutterBottom = false,
	noWrap = false,
	className,
	children,
	isTruncated: _isTruncated,
	...props
}, ref) => {
	const Component = (component ?? variantElements[variant]) as ElementType;
	// className first on purpose: twMerge is last-wins, so the design system owns type and color
	// while layout utilities from the caller still pass through. Moving it last reopens the escape hatch.
	const classes = cn(
		className,
		variantStyles[variant],
		align && alignStyles[align],
		colorStyles[color],
		gutterBottom && "mb-4",
		noWrap && "truncate"
	);

	return (
		<Component ref={ref} className={classes} {...props}>
			{children}
		</Component>
	);
});

TypographyBase.displayName = "TypographyBase";

const TypographyWithTruncation = forwardRef<HTMLElement, TypographyProps>(({className, children, ...props}, ref) => {
	const internalRef = useRef<HTMLElement>(null);
	const isClipped = useIsTruncated(internalRef);
	const setRefs = (el: HTMLElement | null) => {
		internalRef.current = el;
		if (typeof ref === "function") ref(el);
		else if (ref) (ref as {current: HTMLElement | null}).current = el;
	};

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<TypographyBase {...props} ref={setRefs} noWrap className={cn("block", className)}>
					{children}
				</TypographyBase>
			</TooltipTrigger>
			{isClipped && <TooltipContent>{children}</TooltipContent>}
		</Tooltip>
	);
});

TypographyWithTruncation.displayName = "TypographyWithTruncation";

const variantElements: Record<TypographyVariant, ElementType> = {
	heading1: "h1",
	heading2: "h2",
	heading3: "h3",
	subtitle1: "p",
	subtitle2: "p",
	overline1: "p",
	overline2: "p",
	body1: "p",
	body2: "p",
	body3: "p",
	small1: "small",
	small2: "small",
	tag1: "span",
	tag2: "span"
};

// 32px and 11px are off Tailwind's scale so they carry an explicit leading; the rest take the
// line-height Tailwind pairs with the size class.
const variantStyles: Record<TypographyVariant, string> = {
	heading1: "text-[32px] font-bold leading-10",
	heading2: "text-2xl font-normal",
	heading3: "text-xl font-medium",
	subtitle1: "text-lg font-bold",
	subtitle2: "text-lg font-medium",
	overline1: "text-base font-bold",
	overline2: "text-base font-normal",
	body1: "text-sm font-bold",
	body2: "text-sm font-medium",
	body3: "text-sm font-normal",
	small1: "text-xs font-medium",
	small2: "text-xs font-normal",
	tag1: "text-[11px] font-bold leading-4",
	tag2: "text-[11px] font-medium leading-4"
};

const alignStyles: Record<TypographyAlign, string> = {
	left: "text-left",
	center: "text-center",
	right: "text-right",
	justify: "text-justify"
};

const colorStyles: Record<TypographyColor, string> = {
	primary: "text-brand-main-light",
	// A Fixed token, so this stays white under both themes — textPrimary follows the theme and turns
	// black on light. For text that sits on a saturated fill of its own, like Tag's label.
	white: "text-foreground-white",
	textPrimary: "text-foreground-contrast",
	textSecondary: "text-foreground-200",
	textMuted: "text-foreground-300",
	critical: "text-tone-red",
	medium: "text-tone-orange",
	low: "text-tone-yellow",
	success: "text-tone-green",
	info: "text-tone-light-blue"
};

export type TypographyVariant =
	"heading1" | "heading2" | "heading3" |
	"subtitle1" | "subtitle2" | "overline1" | "overline2" |
	"body1" | "body2" | "body3" |
	"small1" | "small2" |
	"tag1" | "tag2";

export type TypographyAlign = "left" | "center" | "right" | "justify";

export type TypographyColor =
	"primary" | "white" |
	"textPrimary" | "textSecondary" | "textMuted" |
	"critical" | "medium" | "low" | "success" | "info";
